/**
 * Task 204 — the bookmark "arrow of light" flight.
 *
 * A full-viewport, pointer-events:none overlay (so it never blocks reading or
 * shifts layout). On a save, a small ember launches from the menu/selection
 * region, arcs like a loosed arrow along a quadratic path, brightening as it
 * flies, and lands at the top-right of the selection where a gold spark blooms —
 * cohesive with the chat's arrow/charge/release light language.
 *
 * Geometry over CSS motion-path: we sample the quadratic Bézier in JS and feed
 * the points to the Web Animations API as transform keyframes. This lands on the
 * exact target coordinates (from Range rects) on every browser, with no reliance
 * on `offset-path` support, and the tangent gives the ember its heading rotation.
 *
 * Reduced motion: no flight, no fire — just a calm spark fade at the landing
 * point, then done. The persistent bookmark pin (rendered by the reader) is the
 * lasting marker either way.
 */
import { useEffect, useRef, useState, type JSX } from "react";

export interface Flight {
  /** Bumped per save so the overlay remounts and replays. */
  key: number;
  from: { x: number; y: number };
  to: { x: number; y: number };
}

const FLIGHT_MS = 620;
const SPARK_MS = 460;

function quad(
  from: { x: number; y: number },
  control: { x: number; y: number },
  to: { x: number; y: number },
  t: number,
): { x: number; y: number } {
  const mt = 1 - t;
  return {
    x: mt * mt * from.x + 2 * mt * t * control.x + t * t * to.x,
    y: mt * mt * from.y + 2 * mt * t * control.y + t * t * to.y,
  };
}

export function BookmarkFlight(props: {
  flight: Flight;
  reducedMotion: boolean;
  onDone: () => void;
}): JSX.Element {
  const { flight, reducedMotion, onDone } = props;
  const emberRef = useRef<HTMLDivElement>(null);
  const [sparked, setSparked] = useState(reducedMotion);

  useEffect(() => {
    if (reducedMotion) {
      // Calm path: skip the flight, just bloom the spark, then finish.
      const t = window.setTimeout(onDone, SPARK_MS);
      return () => window.clearTimeout(t);
    }
    const ember = emberRef.current;
    const { from, to } = flight;
    if (ember === null || typeof ember.animate !== "function") {
      // No WAAPI — degrade to the calm spark.
      setSparked(true);
      const t = window.setTimeout(onDone, SPARK_MS);
      return () => window.clearTimeout(t);
    }

    // Lift the control point above the chord so the arc reads as a loosed arrow,
    // scaled to the distance and biased upward (negative Y is up).
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.hypot(dx, dy);
    const lift = Math.min(160, Math.max(40, dist * 0.4));
    const control = { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - lift };

    const STEPS = 28;
    const frames: Keyframe[] = [];
    for (let i = 0; i <= STEPS; i += 1) {
      const t = i / STEPS;
      const p = quad(from, control, to, t);
      const ahead = quad(from, control, to, Math.min(1, t + 0.02));
      const angle = (Math.atan2(ahead.y - p.y, ahead.x - p.x) * 180) / Math.PI;
      // Brighten + grow toward mid-flight, taper into the landing.
      const ignite = Math.sin(Math.min(1, t * 1.15) * Math.PI);
      const scale = 0.5 + ignite * 0.8;
      const opacity = t < 0.9 ? 0.35 + ignite * 0.65 : 0.9 * (1 - (t - 0.9) / 0.1);
      frames.push({
        transform: `translate(${p.x}px, ${p.y}px) rotate(${angle}deg) scale(${scale})`,
        opacity,
        offset: t,
      });
    }

    const anim = ember.animate(frames, {
      duration: FLIGHT_MS,
      easing: "cubic-bezier(0.16, 0.84, 0.28, 1)",
      fill: "forwards",
    });
    let sparkTimer = 0;
    let doneTimer = 0;
    const onFinish = (): void => {
      setSparked(true);
      sparkTimer = window.setTimeout(() => {
        doneTimer = window.setTimeout(onDone, SPARK_MS);
      }, 0);
    };
    anim.addEventListener("finish", onFinish);
    return () => {
      anim.removeEventListener("finish", onFinish);
      anim.cancel();
      window.clearTimeout(sparkTimer);
      window.clearTimeout(doneTimer);
    };
    // flight.key drives the remount; from/to are read off the fresh flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flight.key, reducedMotion]);

  return (
    <div className="creader-flight" aria-hidden="true">
      {!reducedMotion ? (
        <div
          ref={emberRef}
          className="creader-flight__ember"
          style={{ left: 0, top: 0, transform: `translate(${flight.from.x}px, ${flight.from.y}px) scale(0.5)` }}
        />
      ) : null}
      {sparked ? (
        <div
          className="creader-flight__spark"
          style={{ left: `${flight.to.x}px`, top: `${flight.to.y}px` }}
        />
      ) : null}
    </div>
  );
}
